import React from 'react';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Row, Column } from 'components/shared/Layout';
import FormattedCurrency from 'components/ui/FormattedCurrency';

import { VerticalLine, StyledPrice, StyledHr } from './ExtrasHotels.styles';
import { HOTEL_BOOKING } from 'store/queries';

const HotelsReviewBooking = ({ hotel, children }) => {
  const { loading, data } = useQuery(HOTEL_BOOKING, {
    variables: {
      reference: hotel.eurostarReference
    },
    skip: Boolean(hotel.jacTravelRef)
  });

  if (loading) {
    return null;
  }

  const jacTravelRef = hotel.jacTravelRef || data.hotelBooking?.bookingCode;

  return (
    <div key={jacTravelRef} data-cy="extras-hotel" data-testid="extras-hotel">
      <Row span={15}>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.passenger" />
            </strong>
            <div>
              {hotel.passenger.firstName} {hotel.passenger.lastName}
            </div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.checkInDate" />
            </strong>
            <div>{hotel.checkInDate}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.jacTravelReference" />
            </strong>
            <div>{jacTravelRef}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.id" />
            </strong>
            <div>{hotel.hotelId}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <strong>
            <FormattedMessage id="extras.hotel.eurostarPackageRef" />
          </strong>
          <div>{hotel.eurostarReference}</div>
        </Column>
      </Row>
      <StyledHr />
      <Row span={15}>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.hotelName" />
            </strong>
            <div>{hotel.hotelName || '-'}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.numberOfNights" />
            </strong>
            <div>{hotel.numberOfNights || '-'}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.guestCount" />
            </strong>
            <div>{hotel.guestCount || '-'}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <VerticalLine>
            <strong>
              <FormattedMessage id="extras.hotel.roomCount" />
            </strong>
            <div>{hotel.roomCount || '-'}</div>
          </VerticalLine>
        </Column>
        <Column span={3}>
          <strong>
            <FormattedMessage id="extras.hotel.hotelAddress" />
          </strong>
          <div>{hotel.hotelAddress || '-'}</div>
        </Column>
      </Row>
      <StyledHr />
      <Row span={16}>
        <StyledPrice>
          <strong>
            <FormattedMessage id="extras.hotel.modal.price" />:{' '}
          </strong>
          <FormattedCurrency currency={hotel.currency} amount={hotel.price} />
        </StyledPrice>
      </Row>
      <Row span={16}>
        <StyledPrice>
          <strong>
            <FormattedMessage id="extras.hotel.margin" />:{' '}
          </strong>
          <FormattedCurrency currency={hotel.currency} amount={hotel.margin} />
        </StyledPrice>
      </Row>
      <Row span={16}>
        <StyledPrice>
          <strong>
            <FormattedMessage id="extras.total" />:{' '}
          </strong>
          <FormattedCurrency currency={hotel.currency} amount={hotel.priceWithMargin - hotel.deprecatedTrainMargin} />
        </StyledPrice>
      </Row>
      {children ? React.cloneElement(children, { jacTravelRef }) : null}
    </div>
  );
};

HotelsReviewBooking.propTypes = {
  hotel: PropTypes.shape({
    cancelled: PropTypes.bool,
    passenger: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string
    }),
    checkInDate: PropTypes.string,
    jacTravelRef: PropTypes.string,
    hotelId: PropTypes.string,
    hotelName: PropTypes.string,
    numberOfNights: PropTypes.number,
    guestCount: PropTypes.number,
    roomCount: PropTypes.number,
    hotelAddress: PropTypes.string,
    eurostarReference: PropTypes.string,
    currency: PropTypes.string,
    price: PropTypes.number,
    deprecatedTrainMargin: PropTypes.number,
    margin: PropTypes.number,
    priceWithMargin: PropTypes.number,
    itemRef: PropTypes.string.isRequired,
    bookerSurname: PropTypes.string.isRequired
  }),
  children: PropTypes.node
};

export default HotelsReviewBooking;
